import React, { useEffect, useContext, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Divider, ListItem } from "@material-ui/core";
import { FormattedDate } from "react-intl";
import { makeStyles } from "@material-ui/core/styles";
import { setIncompleteCommentError, setCompleteCommentError } from "../../redux/workorder/workorderActions";
import { useLocalizedName } from "../../globals/langHelpers";

import { FormContext } from "./form";

/*
    Form element for Complete job biling page
    props:
        - field: string, property name for form data
        - label: string, label for form entry
        - editorProps: (props) => component, the input field for the form element
        - required: bool, whether or not the form element is required
        - displayValue?: bool, whether or not the form element will show the selected/entered value
        - subtitle?: string, subtitle to display below label.
        - divider?: bool, whether or not to render a bottom divider
        - characterCountEnabled?: bool, whether or not the form element will display a character counter
*/

export const useFormStyles = makeStyles((theme) => ({
    formElement: {
        border: "none",
        //padding: '16px 0px',
        fontFamily: "Arial",
        lineHeight: "16px",
        fontSize: "14px",
        display: "flex",
        width: "100%",
        backgroundColor: theme.palette.background.paper,
    },
    formInfo: {
        marginRight: "auto",
        marginTop: "auto",
        marginBottom: "auto",
        overflowWrap: "anywhere",
    },
    infoHeader: {
        fontWeight: "bold",
        paddingBottom: "4px",
        color: theme.palette.text.primary, //'var(--text-dark)'
    },
    required: {
        color: "var(--text-error)",
    },
    infoContent: {
        color: "var(--text-medium)", //theme.palette.text.secondary
        padding: 2,
        wordBreak: "break-word",
    },
    formBtn: {
        marginBottom: "auto",
        marginTop: "auto",
        padding: 0,
    },
    editBtn: {
        width: 18,
        height: 18,
    },
    subtitle: {
        color: "#999999",
    },
    newSubTitle: {
        color: "#999999",
    },
}));

export const FormField = (props) => {
    const {
        field,
        label,
        editorProps,
        required,
        disabled,
        displayValue = true,
        subtitle = "",
        divider = true,
        characterCountEnabled = false,
        newSubTitle = "",
    } = props;
    const classes = useFormStyles();
    const { getLocalizedName } = useLocalizedName();
    const formServices = useContext(FormContext);
    const [value, setValue] = useState(formServices.value[field] ?? subtitle);

    useEffect(() => {
        // If the form is changed from outside the current Field, then we need to update it.
        // The main cause of this is a change in the parent reason for incompleting a task as a labourer
        if (value !== formServices.value[field]) {
            setValue(formServices.value[field] ?? value);
        }
    }, [formServices]);

    const onChange = (event, passValue = null) => {
        var val = event.target ? event.target.value : "";
        if (!event.target) {
            val = event;
            if (props.onChange) props.onChange(val);
        } else if (passValue) {
            val = passValue;

            // IF the main reason changes, we need to force the user to re-select a sub reason
            if (
                passValue.reasonCodeType &&
                !passValue.parentReasonCode &&
                formServices.value["incompleteSubReason"] &&
                formServices.value["incompleteSubReason"].parentReasonCode !== passValue.id
            ) {
                formServices.value["incompleteSubReason"] = null;
            }
        } else {
            props.setIncompleteCommentError(val === "");
        }

        formServices.value[field] = val;
        setValue(val);
    };

    return (
        <>
            <ListItem disabled={disabled}>
                <div className={classes.formElement}>
                    <div className={classes.formInfo}>
                        <div className={classes.infoHeader}>
                            {required && <span className={classes.required}>*</span>}
                            {label}
                        </div>
                        {displayValue && value && (
                            <div className={classes.infoContent}>
                                {field === "date" || field === "newDueDate" || field === "dueDate" ? (
                                    <FormattedDate
                                        value={value}
                                        year="numeric"
                                        month="long"
                                        day="2-digit"
                                        weekday="long"
                                        hour="numeric"
                                        minute="numeric"
                                        hour12={false}
                                        timeZoneName="short"
                                    />
                                ) : value.name_en || value.nameEn ? (
                                    getLocalizedName({ object: value })
                                ) : (
                                    value.toString()
                                )}
                            </div>
                        )}
                        {subtitle !== "" && !value && <div className={classes.subtitle}>{subtitle}</div>}
                        {newSubTitle !== "" && !value && <div className={classes.newSubTitle}>{newSubTitle}</div>}
                    </div>
                    {editorProps &&
                        formServices.edit({
                            ...editorProps,
                            required,
                            disabled,
                            value,
                            onChange,
                            characterCount: characterCountEnabled ? (value ? value.length : "0") : null,
                        })}
                </div>
            </ListItem>
            {divider && <Divider />}
        </>
    );
};

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            setIncompleteCommentError,
            setCompleteCommentError,
        },
        dispatch,
    );

export default connect(null, mapDispatchToProps)(FormField);
